/**
 * Configuracao da analise de credito: o catalogo descritivo dos parametros do motor,
 * as versoes de parametros, a comparacao entre versoes e a heranca do padrao global.
 *
 * A tela da empresa e a pagina do padrao global em /admin usam o mesmo editor. Cada
 * escopo tem o proprio conjunto de rotas, com as mesmas formas, e o editor recebe o
 * conjunto pela interface `CreditParameterApi`.
 *
 * As rotas de governanca (vigencia futura, cancelamento do agendamento, aprovacoes,
 * origem do rascunho, heranca e padrao global) sao as da governanca dos parametros
 * (ADR 0082). Onde a instalacao ainda nao as tem, a resposta e 404 e a tela segue com
 * as rotas que existem.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { api } from '../client';

/** Tipo de editor de cada parametro, do catalogo descritivo. */
export type CreditParameterKind =
  | 'booleano'
  | 'inteiro'
  | 'dinheiro'
  | 'opcao'
  | 'lista'
  | 'mapa'
  | 'tabela'
  | 'estrutura';

/** Grupo funcional da tela, na ordem de apresentacao. */
export interface CreditParameterGroup {
  key: string;
  label: string;
  description: string;
}

/**
 * Descricao de um parametro.
 *
 * Nos parametros simples, `nullable` diz se o parametro aceita "em branco" (nulo);
 * nos compostos (mapa, tabela), se cada valor ou celula aceita. Em branco nunca e
 * zero.
 */
export interface CreditParameterSpec {
  key: string;
  group: string;
  label: string;
  help?: string;
  kind: CreditParameterKind;
  unit?: string;
  nullable: boolean;
  options?: string[];
  rules?: string[];
  source?: string;
  classification?: string;
  order: number;
}

/** Regra do catalogo do motor, para a lista de regras homologadas. */
export interface CreditRuleSpec {
  id: string;
  step: number;
  name: string;
  kind: string;
  classification: string;
  conflicts?: string[];
  evaluated: boolean;
  availability: string;
  parameters: string[] | null;
  premise?: string;
}

export interface CreditParameterCatalog {
  engine_version: string;
  groups: CreditParameterGroup[];
  parameters: CreditParameterSpec[];
  rules: CreditRuleSpec[];
  /** Nome legivel dos valores de dominio, como "execucao_obras". */
  value_labels: Record<string, string>;
}

/** Situacao de uma versao. `agendada` e a ativada com vigencia futura. */
export type CreditParameterStatus = 'rascunho' | 'agendada' | 'ativa' | 'revogada';

/** Valores de uma versao, pelo nome JSON da especificacao 41. */
export type CreditParameterValues = Record<string, unknown>;

/**
 * Por que a versao foi revogada: substituida por outra que passou a valer, ou
 * agendamento cancelado antes da data (a versao nunca valeu). Vazio fora da revogada.
 */
export type CreditRevocationKind = 'substituida' | 'agendamento_cancelado' | '';

/**
 * Uma aprovacao registrada. Com mais de um aprovador exigido pela plataforma, as
 * aprovacoes parciais aparecem enquanto a versao continua em rascunho; editar o
 * rascunho descarta as aprovacoes dadas.
 */
export interface CreditParameterApproval {
  label: string;
  at: string;
  note: string;
  /** Inicio de vigencia pedido nesta aprovacao. Nulo, vale ao completar. */
  requested_effective_from: string | null;
  /** Aprovacao de quem criou ou editou a versao, pela liberacao nominal. */
  autoaprovacao?: boolean;
}

export interface CreditParameterSet {
  id: string;
  version: number;
  status: CreditParameterStatus;
  values_hash: string;
  source: string;
  based_on_version: number | null;
  notes: string;
  created_label: string;
  updated_label: string;
  activated_label: string;
  activated_at: string | null;
  activation_note: string;
  /** Inicio da vigencia. Futuro, a versao esta agendada. */
  effective_from: string | null;
  /** Fim da vigencia: o instante em que outra versao passou a valer. */
  effective_until: string | null;
  revoked_label: string;
  revoked_at: string | null;
  revocation_kind: CreditRevocationKind;
  /** Justificativa do cancelamento do agendamento. */
  revocation_note: string;
  /** Versao do padrao global de onde os valores vieram (copia ou heranca). */
  based_on_global_version: number | null;
  approvals: CreditParameterApproval[];
  /** Quem pediu ja aprovou esta versao: a proxima aprovacao e de outra pessoa. */
  requester_has_approved: boolean;
  created_at: string;
  updated_at: string;
  /** Quem pediu criou ou editou a versao, e por isso nao pode ativa-la. */
  requester_is_author: boolean;
  /**
   * Quem pediu e autor e esta liberado nominalmente para ativar mesmo assim
   * (autoaprovacao). So vem verdadeiro junto com requester_is_author.
   */
  requester_may_self_approve?: boolean;
  homologated_rules: string[];
  /** Ausente na listagem. */
  values?: CreditParameterValues;
}

/** De onde o rascunho novo copia os valores (contrato da governanca). */
export type CreditDraftSource = 'ativa' | 'padrao_global';

export interface CreditDraftInput {
  notes?: string;
  /** Omitido, o servidor copia a versao ativa, ou os valores documentados. */
  source?: CreditDraftSource;
}

export interface CreditDraftUpdateInput {
  /** So os parametros alterados: cada chave substitui o parametro inteiro. */
  values?: CreditParameterValues;
  notes?: string;
}

export interface CreditActivationInput {
  justification: string;
  /** Data e hora de inicio da vigencia, em ISO 8601. Omitida, ativa agora. */
  effective_from?: string;
}

export interface CreditScheduleCancellationInput {
  justification: string;
}

export interface CreditParameterSetRef {
  id: string;
  version: number;
  status: string;
  values_hash: string;
}

export interface CreditParameterDiffEntry {
  key: string;
  group: string;
  label: string;
  base: unknown;
  other: unknown;
  changed: boolean;
}

export interface CreditParameterDiff {
  base: CreditParameterSetRef;
  other: CreditParameterSetRef;
  changed_count: number;
  parameters: CreditParameterDiffEntry[];
}

export type CreditInheritanceMode = 'propria' | 'herda_padrao_global';

/** A versao do padrao global em vigor, como a empresa a ve. */
export interface CreditGlobalDefault {
  id: string;
  version: number;
  values_hash: string;
  effective_from: string | null;
}

/** Aviso da heranca, como a versao global que esperou o rascunho aberto. */
export interface CreditInheritanceNotice {
  global_version: number;
  draft_version: number;
  kind: string;
  message: string;
  created_at: string;
}

export interface CreditInheritance {
  mode: CreditInheritanceMode;
  updated_label?: string;
  updated_at?: string | null;
  /** Padrao global em vigor agora, ou nulo. */
  global_default?: CreditGlobalDefault | null;
  /** Maior versao global de onde vieram valores da empresa. */
  received_global_version?: number | null;
  /** Versao global em vigor que ainda espera o rascunho aberto da empresa. */
  pending_global_version?: number | null;
  notices?: CreditInheritanceNotice[];
}

/** As rotas de um escopo: a empresa da sessao ou o padrao global da plataforma. */
export interface CreditParameterApi {
  catalog: (signal?: AbortSignal) => Promise<CreditParameterCatalog>;
  list: (signal?: AbortSignal) => Promise<CreditParameterSet[]>;
  get: (id: string, signal?: AbortSignal) => Promise<CreditParameterSet>;
  createDraft: (input: CreditDraftInput) => Promise<CreditParameterSet>;
  updateDraft: (
    id: string,
    input: CreditDraftUpdateInput,
  ) => Promise<CreditParameterSet>;
  activate: (id: string, input: CreditActivationInput) => Promise<CreditParameterSet>;
  /** Cancela o agendamento antes da data: a versao agendada passa a revogada. */
  cancelSchedule: (
    id: string,
    input: CreditScheduleCancellationInput,
  ) => Promise<CreditParameterSet>;
  diff: (
    id: string,
    withId: string,
    signal?: AbortSignal,
  ) => Promise<CreditParameterDiff>;
}

function rotasDoEscopo(catalogo: string, base: string): CreditParameterApi {
  return {
    catalog: (signal) => api.get<CreditParameterCatalog>(catalogo, signal),
    list: (signal) => api.get<CreditParameterSet[]>(base, signal),
    get: (id, signal) =>
      api.get<CreditParameterSet>(`${base}/${encodeURIComponent(id)}`, signal),
    createDraft: (input) => api.post<CreditParameterSet>(base, input),
    updateDraft: (id, input) =>
      api.patch<CreditParameterSet>(`${base}/${encodeURIComponent(id)}`, input),
    activate: (id, input) =>
      api.post<CreditParameterSet>(
        `${base}/${encodeURIComponent(id)}/activation`,
        input,
      ),
    cancelSchedule: (id, input) =>
      api.post<CreditParameterSet>(
        `${base}/${encodeURIComponent(id)}/schedule-cancellation`,
        input,
      ),
    diff: (id, withId, signal) =>
      api.get<CreditParameterDiff>(
        `${base}/${encodeURIComponent(id)}/diff?with=${encodeURIComponent(withId)}`,
        signal,
      ),
  };
}

/** Parametros do motor da empresa da sessao. */
export const creditParameters = {
  ...rotasDoEscopo(
    '/api/v1/credit-engine/parameter-catalog',
    '/api/v1/credit-engine/parameter-sets',
  ),
  /** Heranca do padrao global. 404 enquanto a instalacao nao tiver a rota. */
  inheritance: (signal?: AbortSignal) =>
    api.get<CreditInheritance>('/api/v1/credit-engine/inheritance', signal),
  setInheritance: (mode: CreditInheritanceMode) =>
    api.put<CreditInheritance>('/api/v1/credit-engine/inheritance', { mode }),
};

/** Padrao global, administrado pela plataforma. */
export const platformCreditParameters: CreditParameterApi = rotasDoEscopo(
  '/api/v1/platform/credit-engine/parameter-catalog',
  '/api/v1/platform/credit-engine/parameter-sets',
);
